import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  MultiSelectionAutocomplete,
  SingleSelectionAutocomplete,
} from "./SelectionAutocomplete";

const LEAD_STATUS_OPTIONS = [
  { value: "ACCEPT", label: "Lead" },
  { value: "NOT ACCEPT", label: "Discard" },
  { value: "FUZZY", label: "Unsure" },
];
const UPDATE_CATEGORY_OPTIONS = ["New Company", "Updates", "Reclassified", "Archived"];
const SORT_OPTIONS = [
  { value: "", label: "None" },
  { value: "name-asc", label: "Name (A to Z)" },
  { value: "name-desc", label: "Name (Z to A)" },
  { value: "rank-asc", label: "Crunchbase Rank (Low to High)" },
  { value: "rank-desc", label: "Crunchbase Rank (High to Low)" },
  { value: "employees-desc", label: "Employees (High to Low)" },
  { value: "employees-asc", label: "Employees (Low to High)" },
];
const MANUAL_CHANGE_OPTIONS = [
  { value: "", label: "All" },
  { value: "manual-only", label: "Manual Changes Only" },
  { value: "no-manual", label: "No Manual Changes" },
];

function FilterAccordion({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      elevation={0}
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: "14px !important",
        backgroundColor: "#f8fafc",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.25,
          minHeight: 46,
          "& .MuiAccordionSummary-content": {
            my: 0.75,
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#1f2937" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
            {subtitle}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function SortBar({
  companies = [],
  sortBy,
  setSortBy,
  selectedLeadStatus,
  setSelectedLeadStatus,
  selectedUpdateCategory,
  setSelectedUpdateCategory,
  selectedManualChange,
  setSelectedManualChange,
  leadStatusFilterDisabled,
}) {
  const handleUpdateCategoryChange = (nextSelection) => {
    if (nextSelection.includes("Archived")) {
      setSelectedUpdateCategory(["Archived"]);
      return;
    }

    if (selectedUpdateCategory.includes("Archived")) {
      setSelectedUpdateCategory(nextSelection.filter((option) => option !== "Archived"));
      return;
    }

    setSelectedUpdateCategory(nextSelection);
  };

  const leadStatusCounts = new Map();
  const updateCategoryCounts = new Map();

  companies.forEach((company) => {
    const leadType = company["Lead Type"];
    if (leadType) {
      leadStatusCounts.set(leadType, (leadStatusCounts.get(leadType) || 0) + 1);
    }

    if (company.Archived) {
      updateCategoryCounts.set("Archived", (updateCategoryCounts.get("Archived") || 0) + 1);
      return;
    }

    (company["Update Categories"] || []).forEach((category) => {
      updateCategoryCounts.set(category, (updateCategoryCounts.get(category) || 0) + 1);
    });
  });

  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gap: 1,
      }}
    >
      <FilterAccordion title="Sorting" subtitle="Ordering and manual changes">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1.25,
            width: "100%",
          }}
        >
          <SingleSelectionAutocomplete
            label="Sort By"
            value={sortBy}
            onChange={setSortBy}
            options={SORT_OPTIONS}
          />
          <SingleSelectionAutocomplete
            label="Manual Change"
            value={selectedManualChange}
            onChange={setSelectedManualChange}
            options={MANUAL_CHANGE_OPTIONS}
          />
        </Box>
      </FilterAccordion>

      <FilterAccordion title="Status Filters" subtitle="Lead type and update category">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1.25,
            width: "100%",
          }}
        >
          <MultiSelectionAutocomplete
            label="Lead Status"
            values={selectedLeadStatus}
            onChange={setSelectedLeadStatus}
            options={LEAD_STATUS_OPTIONS}
            counts={leadStatusCounts}
            showCheckboxes
          />
          <MultiSelectionAutocomplete
            label="Update Category"
            values={selectedUpdateCategory}
            onChange={handleUpdateCategoryChange}
            options={UPDATE_CATEGORY_OPTIONS.map((option) => ({
              value: option,
              label: option,
            }))}
            counts={updateCategoryCounts}
            showCheckboxes
          />
        </Box>
      </FilterAccordion>
    </Box>
  );
}
